import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Contacts from "@/pages/Contacts";
import Clients from "@/pages/Clients";
import Finance from "@/pages/Finance";
import Invoices from "@/pages/Invoices";
import Users from "@/pages/Users";
import Pipeline from "@/pages/Pipeline";
import Followups from "@/pages/Followups";
import Proposals from "@/pages/Proposals";
import Projects from "@/pages/Projects";
import Activities from "@/pages/Activities";
import Discovery from "@/pages/Discovery";
import FollowupSettings from "@/pages/FollowupSettings";
import Layout from "@/components/Layout";
import "@/App.css";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-muted-foreground text-sm">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/" replace />;
    return children;
}

function ThemeInit() {
    useEffect(() => {
        const stored = localStorage.getItem("zbz-theme");
        if (stored === "dark") document.documentElement.classList.add("dark");
    }, []);
    return null;
}

function App() {
    return (
        <AuthProvider>
            <ThemeInit />
            <Toaster position="bottom-right" richColors closeButton />
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                    <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                    <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/leads" element={<Leads />} />
                        <Route path="/contacts" element={<Contacts />} />
                        <Route path="/clients" element={<Clients />} />
                        <Route path="/invoices" element={<Invoices />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/pipeline" element={<Pipeline />} />
                        <Route path="/followups" element={<Followups />} />
                        <Route path="/proposals" element={<Proposals />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/expenses" element={<Finance key="expenses" />} />
                        <Route path="/project-pricing" element={<Finance key="pricing" mode="pricing" />} />
                        <Route path="/discovery" element={<Discovery />} />
                        <Route path="/activities" element={<Activities />} />
                        <Route path="/settings/followup-automation" element={<FollowupSettings />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
